import {ComponentType, lazy, LazyExoticComponent} from 'react';

import {APIDatePicker} from '../APIDatePicker/APIDatePicker';
import {Checkbox} from '../Checkbox/Checkbox';
import {Checkboxes} from '../Checkboxes/Checkboxes';
import {Chips} from '../Chip/Chips';
import {Choice} from '../Choice/Choice';
import {AsyncChoice} from '../Choice/components/AsyncChoice';
import {CreatableChoice} from '../Choice/components/CreatableChoice';
import {CreatableMultiChoice} from '../Choice/components/CreatableMultiChoice';
import {CurrencyInput} from '../CurrencyInput/CurrencyInput';
import {DatePicker} from '../DatePicker/DatePicker';
import {DateTimePicker} from '../DateTimePicker/DateTimePicker';
import {DayMonthPicker} from '../DayMonthPicker/DayMonthPicker';
import {EmailInput} from '../EmailInput/EmailInput';
import {HiddenInput} from '../HiddenInput/HiddenInput';
import {IntegerInput} from '../IntegerInput/IntegerInput';
import {MultiChoice} from '../MultiChoice/MultiChoice';
import {NumberInput} from '../NumberInput/NumberInput';
import {NumberInputWithOptions} from '../NumberInputWithOptions/NumberInputWithOptions';
import {PasswordInput} from '../PasswordInput/PasswordInput';
import {Radio} from '../Radio/Radio';
import {Slider} from '../Slider/Slider';
import {Switch} from '../Switch/Switch';
import {Textarea} from '../Textarea/Textarea';
import {TextInput} from '../TextInput/TextInput';
import {TimePicker} from '../TimePicker/TimePicker';
import {FormFieldType} from './types';

/**
 * Form-control registry.
 *
 * A handful of controls own large, self-contained dependencies that most forms
 * never use — the rich-text editor (quill), the lexical mentions/suggestion
 * editors, the phone-number parser (libphonenumber), and the image
 * upload/cropper. These are code-split with `React.lazy`, so those dependencies
 * are NOT pulled into a consumer's initial bundle; they load on demand the first
 * time a form renders that field type. An app that never renders them (e.g. the
 * Online Offer app) automatically tree-shakes them out — no per-app config.
 *
 * Everything else stays eager: light controls, and common controls whose deps
 * are shared across the app anyway (date pickers → date-fns, currency →
 * decimal.js). Lazy-loading those would add a first-render delay to very common
 * fields for little bundle benefit. `FormField` renders controls inside a
 * `Suspense` boundary, so the eager controls never suspend.
 */

// Controls have heterogeneous props, so the registry is intentionally untyped on
// props (matching the FormField dispatch). Eager and lazily-loaded controls are
// both valid element types for FormField's `<Component />`.
type ControlComponent = ComponentType<any> | LazyExoticComponent<ComponentType<any>>;

export const formControls: {[key in FormFieldType]: ControlComponent} = {
  // Eager — light controls and common controls with shared dependencies.
  text: TextInput,
  number: NumberInput,
  currency: CurrencyInput,
  integer: IntegerInput,
  password: PasswordInput,
  email: EmailInput,
  switch: Switch,
  radio: Radio,
  textarea: Textarea,
  checkbox: Checkbox,
  checkboxes: Checkboxes,
  chips: Chips,
  choice: Choice,
  asyncChoice: AsyncChoice,
  creatableChoice: CreatableChoice,
  multiChoice: MultiChoice,
  creatableMultiChoice: CreatableMultiChoice,
  date: DatePicker,
  apiDate: APIDatePicker,
  dateTime: DateTimePicker,
  time: TimePicker,
  dayMonth: DayMonthPicker,
  slider: Slider,
  numberInputWithOptions: NumberInputWithOptions,
  hidden: HiddenInput,

  // Lazy — rare controls with large, self-contained dependencies.
  richText: lazy(() =>
    import('../RichText/RichText').then((module) => ({default: module.RichText}))
  ),
  suggestionInput: lazy(() =>
    import('../SuggestionInput/SuggestionInput').then((module) => ({
      default: module.SuggestionInput,
    }))
  ),
  mentionsInput: lazy(() =>
    import('../MentionsInput/MentionsInput').then((module) => ({default: module.MentionsInput}))
  ),
  phone: lazy(() =>
    import('../PhoneInput/PhoneInput').then((module) => ({default: module.PhoneInput}))
  ),
  uploadImage: lazy(() =>
    import('../UploadImage/UploadImage').then((module) => ({default: module.UploadImage}))
  ),
};
